import { Box, Button, FileButton, Image, LoadingOverlay } from "@dehub/theme";
import { ImageSquareIcon } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MediaApi } from "@/apis";
import { message } from "@/utils/message";
import classes from "./image-upload.module.css";

export type IFile = {
  uploading: boolean;
  url?: string;
  file: Blob | null;
  thumbnail?: string;
};

type Props = {
  value?: string | null;
  error?: string;
  onChange?: (file: IFile) => void;
  size?: number;
  radius?: number | string;
};

export function ImageUpload({ onChange, error, value, size, radius }: Readonly<Props>) {
  const { t } = useTranslation("common");
  const [file, setFile] = useState<IFile | null>(null);
  const resetRef = useRef<() => void>(null);

  const onFileUpload = (blob: Blob | null) => {
    if (!blob) return;

    (async () => {
      try {
        const form = new FormData();
        form.append("file", blob);

        const res = await MediaApi.uploadImage(form);

        if (onChange)
          onChange({
            file: blob,
            url: res.url,
            uploading: false,
            thumbnail: res.thumbnail,
          });

        setFile({
          file: blob,
          url: res.url,
          uploading: false,
          thumbnail: res.thumbnail,
        });
      } catch (err: unknown) {
        setFile(null);
        const e = err as { message?: string };
        message.error(e?.message || t("requestFailed"));
      }
    })();

    setFile({
      file: blob,
      uploading: true,
    });
  };

  useEffect(() => {
    value &&
      setFile({
        file: null,
        url: value,
        uploading: false,
      });
  }, [value]);

  return (
    <Box className={classes.multiImageUpload}>
      <FileButton resetRef={resetRef} onChange={onFileUpload} accept="image/png,image/jpeg">
        {(props) =>
          file ? (
            <Box style={{ borderRadius: typeof radius === "string" ? `var(--mantine-radius-${radius})` : (radius ?? 4) }} w={size ?? 80} h={size ?? 80} className={classes.imageBox} {...props}>
              <Image
                className={classes.image}
                width={size ?? 80}
                height={size ?? 80}
                radius={radius}
                src={file.file ? URL.createObjectURL(file.file) : file.url}
                alt="image"
              />
              <LoadingOverlay visible={file.uploading} opacity={0.3} loaderProps={{ size: "sm" }} />
            </Box>
          ) : (
            <Button
              radius={radius}
              w={size ?? 80}
              h={size ?? 80}
              variant="light"
              color="gray"
              className={classes.uploadButton}
              data-error={error || undefined}
              {...props}>
              <ImageSquareIcon size={20} />
            </Button>
          )
        }
      </FileButton>
    </Box>
  );
}
